import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { apiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Wind, Loader2, ShieldCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const SIDE = 'https://images.pexels.com/photos/28860321/pexels-photo-28860321/free-photo-of-exciting-kiteboarding-adventure-on-ocean-waves.jpeg?auto=compress&cs=tinysrgb&w=1280';

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get('token') || '', [params]);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error(t('auth.reset.short')); return; }
    if (password !== confirm) { toast.error(t('auth.reset.mismatch')); return; }
    setBusy(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
      toast.success(t('auth.reset.success'));
      setTimeout(() => navigate('/login'), 1800);
    } catch (err) {
      toast.error(apiError(err, t('auth.reset.invalid')));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="flex items-center justify-center px-4 py-12 order-2 lg:order-1">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-8"><Wind className="w-7 h-7 text-[#00B4D8]" /><span className="font-display text-2xl">KITEIQ</span></Link>

          {!token ? (
            <div data-testid="reset-missing-token">
              <div className="w-12 h-12 rounded-2xl bg-[#FFB020]/15 flex items-center justify-center mb-4">
                <AlertTriangle className="w-6 h-6 text-[#FFB020]" />
              </div>
              <h1 className="text-2xl font-semibold">{t('auth.reset.title')}</h1>
              <p className="text-muted-foreground text-sm mt-2">{t('auth.reset.missingToken')}</p>
              <Link to="/forgot-password">
                <Button className="mt-6 bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl" data-testid="reset-request-new">
                  {t('auth.reset.requestNew')}
                </Button>
              </Link>
            </div>
          ) : done ? (
            <div data-testid="reset-success-state">
              <div className="w-12 h-12 rounded-2xl bg-[#2EE59D]/15 flex items-center justify-center mb-4">
                <ShieldCheck className="w-6 h-6 text-[#2EE59D]" />
              </div>
              <h1 className="text-2xl font-semibold">{t('auth.reset.success')}</h1>
              <Link to="/login">
                <Button className="mt-6 bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl" data-testid="reset-goto-login">
                  {t('auth.reset.goToLogin')}
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-semibold">{t('auth.reset.title')}</h1>
              <p className="text-muted-foreground text-sm mt-1">{t('auth.reset.subtitle')}</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="password">{t('auth.reset.password')}</Label>
                  <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('auth.reset.passwordPlaceholder')} className="mt-1.5 bg-[#12161B] border-white/15" data-testid="reset-password" />
                </div>
                <div>
                  <Label htmlFor="confirm">{t('auth.reset.confirm')}</Label>
                  <Input id="confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    placeholder={t('auth.reset.confirmPlaceholder')} className="mt-1.5 bg-[#12161B] border-white/15" data-testid="reset-confirm" />
                </div>
                <Button type="submit" disabled={busy} className="w-full h-11 bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl" data-testid="reset-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('auth.reset.submit')}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src={SIDE} alt="Kitesurfing" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,10,10,0.4), rgba(10,10,10,0.85))' }} />
        <div className="absolute bottom-10 left-10 right-10">
          <h2 className="font-display text-5xl">{t('auth.register.sideTitle1')}<br /><span className="text-gradient-blue">{t('auth.register.sideTitle2')}</span></h2>
        </div>
      </div>
    </div>
  );
}
